import React from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import Footer from '../components/Footer';
import { APP_DASHBOARD_URL } from '../lib/appUrl';
import {
  ArrowLeft,
  ArrowRight,
  Sparkles,
  Rocket,
  Map,
  BookOpen,
  Zap,
  Target,
  DollarSign,
  Filter,
  Shield,
} from 'lucide-react';

const FEATURES = {
  'ai-prioritization': {
    title: 'AI Prioritization',
    Icon: Rocket,
    tagline: 'Every signal, sorted into Build / Fix / Ignore — with $ math attached.',
    subtitle:
      "VibeSprint's analysis engine categorizes every piece of feedback into one of three lanes, and estimates the revenue impact + effort + ROI/hour so you always ship the right thing next.",
    highlights: [
      {
        Icon: Target,
        title: 'Three-lane clarity',
        body: 'Build Next (revenue opportunities), Fix Now (churn risks), Ignore (noise). No 47-column database, no maybe-tags, no waffling.',
      },
      {
        Icon: DollarSign,
        title: 'Revenue math baked in',
        body: "Every Build-Next decision comes with a projected monthly-revenue lift ($X–$Y/mo range), so you compare apples to apples — not vote counts.",
      },
      {
        Icon: Zap,
        title: 'Quick Wins detection',
        body: 'Automatically flags items with high revenue impact + low effort. Ship those first for maximum leverage.',
      },
      {
        Icon: Filter,
        title: 'Aggressive noise filtering',
        body: 'The AI is opinionated about what deserves your attention. Low-priority typos, vague opinions, and off-topic requests go to Ignore.',
      },
    ],
    ctaLabel: 'Try the AI now',
  },
  roadmap: {
    title: 'Roadmap',
    Icon: Map,
    tagline: 'Your prioritized backlog, always in sync with your decisions.',
    subtitle:
      "Every Build-Next and Fix-Now decision automatically flows into your roadmap, grouped by status: New → Planned → In Progress → Shipped. Update status once, and every other view (dashboard, changelog) reflects instantly.",
    highlights: [
      {
        Icon: Map,
        title: 'Always in sync',
        body: 'Move a decision to “shipped” and it lands on the changelog automatically. No duplicate work, no stale views.',
      },
      {
        Icon: Target,
        title: 'Grouped by lane',
        body: 'See at a glance which lane each planned item came from — Build (revenue) vs Fix (churn) — for balanced execution.',
      },
      {
        Icon: DollarSign,
        title: 'Total pipeline value',
        body: 'The top of your roadmap shows the total projected MRR lift from all planned items. Great motivation on rough days.',
      },
      {
        Icon: Zap,
        title: 'Public sharing',
        body: 'Toggle any workspace to a public URL (vibesprint.ai/r/<workspace>) for build-in-public transparency.',
      },
    ],
    ctaLabel: 'View your roadmap',
  },
  changelog: {
    title: 'Changelog',
    Icon: BookOpen,
    tagline: 'Every shipped decision, automatically turned into a changelog entry.',
    subtitle:
      "Mark a decision as “Shipped” and it lands on your changelog with the original signal, the reasoning, and the outcome. Zero manual writing required. Email notifications to people who asked are not yet available.",
    highlights: [
      {
        Icon: BookOpen,
        title: 'Auto-generated entries',
        body: 'Marking a decision “Shipped” creates a changelog entry with the reasoning, source signal, and expected outcome preserved.',
      },
      {
        Icon: Sparkles,
        title: 'AI-drafted copy',
        body: 'Turn any shipped decision into a polished public changelog post with one click. Never start from a blank page.',
      },
      {
        Icon: Shield,
        title: 'Traceability',
        body: 'Every shipped item links back to the original feedback, so you always know who asked for what.',
      },
      {
        Icon: Zap,
        title: 'Public feed',
        body: 'Public changelog URL + RSS feed so your customers, LinkedIn audience, or /buildinpublic followers stay in the loop.',
      },
    ],
    ctaLabel: 'See the changelog',
  },
};

const FeaturePage = () => {
  const navigate = useNavigate();
  const { slug } = useParams();
  const feat = FEATURES[slug];

  if (!feat) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center p-8 text-center">
        <h1 className="text-3xl font-bold mb-2">Feature not found</h1>
        <p className="text-muted-foreground mb-6">
          We haven't published a feature page for "{slug}" yet.
        </p>
        <Button onClick={() => navigate('/')} data-testid="feature-notfound-home">Back to home</Button>
      </div>
    );
  }

  const HeroIcon = feat.Icon;

  return (
    <div className="min-h-screen bg-background" data-testid={`feature-page-${slug}`}>
      <div className="border-b bg-card sticky top-0 z-50">
        <div className="container-wide mx-auto px-4 py-3 flex items-center justify-between">
          <Button variant="ghost" size="sm" onClick={() => navigate(-1)} data-testid="feature-back-button">
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back
          </Button>
          <Button asChild variant="outline" size="sm">
            <a href={APP_DASHBOARD_URL}>Open dashboard</a>
          </Button>
        </div>
      </div>

      {/* Hero */}
      <section className="py-16 sm:py-20 border-b bg-gradient-to-b from-background via-primary/5 to-background">
        <div className="container mx-auto px-4 max-w-3xl text-center">
          <div className="inline-flex items-center gap-2 rounded-full bg-primary/10 p-2 mb-4">
            <HeroIcon className="h-6 w-6 text-primary" />
          </div>
          <Badge variant="outline" className="mb-4 border-primary/30 text-primary">
            <Sparkles className="h-3 w-3 mr-1" />
            Feature
          </Badge>
          <h1 className="text-4xl sm:text-5xl font-bold tracking-tight mb-4">
            {feat.title}
          </h1>
          <p className="text-xl font-medium mb-4">{feat.tagline}</p>
          <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
            {feat.subtitle}
          </p>
        </div>
      </section>

      {/* Highlights */}
      <section className="py-16">
        <div className="container mx-auto px-4 max-w-5xl">
          <h2 className="text-3xl font-bold text-center mb-12">How it works</h2>
          <div className="grid md:grid-cols-2 gap-5">
            {feat.highlights.map((h, i) => {
              const HIcon = h.Icon;
              return (
                <Card key={i} className="p-6" data-testid={`feature-highlight-${i}`}>
                  <div className="flex items-start gap-3">
                    <div className="rounded-lg bg-primary/10 p-2 flex-shrink-0">
                      <HIcon className="h-5 w-5 text-primary" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-base mb-1">{h.title}</h3>
                      <p className="text-sm text-muted-foreground leading-relaxed">{h.body}</p>
                    </div>
                  </div>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 border-t bg-muted/20">
        <div className="container mx-auto px-4 max-w-2xl text-center">
          <h2 className="text-3xl font-bold mb-3">Ready to try {feat.title}?</h2>
          <p className="text-muted-foreground mb-6">
            Load our sample dataset and see {feat.title.toLowerCase()} in action — no signup required.
          </p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <Button asChild className="btn-cta" size="lg" data-testid="feature-cta-primary">
              <a href={APP_DASHBOARD_URL}>
                {feat.ctaLabel}
                <ArrowRight className="h-4 w-4 ml-2" />
              </a>
            </Button>
            <Button variant="outline" onClick={() => navigate('/pricing')} size="lg" data-testid="feature-cta-pricing">
              View pricing
            </Button>
          </div>
        </div>
      </section>

      <Footer variant="minimal" />
    </div>
  );
};

export default FeaturePage;
